import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./Registration.css";

function Registration() {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const selectedCourse = location.state?.course || null;

  const currentLanguage =
    i18n.resolvedLanguage === "en" ||
    i18n.language?.startsWith("en")
      ? "en"
      : "ar";

  const isArabic = currentLanguage === "ar";

  const [form, setForm] = useState({
    name: "",
    parentName: "",
    phone: "",
    whatsapp: "",
    email: "",
    age: "",
    gender: "",
    area: "",
    course: "",
    education: "",
    previousRegistration: "",
    howFoundUs: "",
    notes: "",
  });

  /* =====================================================
     LANGUAGE
  ===================================================== */

  useEffect(() => {
    document.documentElement.lang = currentLanguage;
    document.documentElement.dir = isArabic ? "rtl" : "ltr";
  }, [currentLanguage, isArabic]);

  /* =====================================================
     SELECTED COURSE
  ===================================================== */

  useEffect(() => {
    if (selectedCourse) {
      setForm((prev) => ({
        ...prev,
        course:
          currentLanguage === "en"
            ? selectedCourse.titleEn ||
              selectedCourse.titleAr ||
              ""
            : selectedCourse.titleAr ||
              selectedCourse.titleEn ||
              "",
      }));
    }
  }, [selectedCourse, currentLanguage]);

  /* =====================================================
     FORM CHANGE
  ===================================================== */

  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  /* =====================================================
     LANGUAGE CHANGE
  ===================================================== */

  const handleLanguageChange = () => {
    const newLanguage = isArabic ? "en" : "ar";

    i18n.changeLanguage(newLanguage);

    document.documentElement.lang = newLanguage;
    document.documentElement.dir =
      newLanguage === "ar" ? "rtl" : "ltr";
  };

  /* =====================================================
     WHATSAPP SUBMIT
  ===================================================== */

  const handleSubmit = (e) => {
    e.preventDefault();

    const genderText =
      form.gender || t("registration.notSpecified");

    const previousText =
      form.previousRegistration ||
      t("registration.notSpecified");

    const foundText =
      form.howFoundUs ||
      t("registration.notSpecified");

    const message = `
🌟 ${t("registration.whatsappTitle")} 🌟

📚 ${t("registration.whatsappCourse")}:
${form.course || t("registration.notSpecified")}

━━━━━━━━━━━━━━

👤 ${t("registration.whatsappName")}:
${form.name}

👨‍👩‍👧 ${t("registration.whatsappParent")}:
${form.parentName || t("registration.notAvailable")}

🎂 ${t("registration.whatsappAge")}:
${form.age}

⚧ ${t("registration.whatsappGender")}:
${genderText}

📍 ${t("registration.whatsappArea")}:
${form.area}

━━━━━━━━━━━━━━

📱 ${t("registration.whatsappPhone")}:
${form.phone}

💬 ${t("registration.whatsappNumber")}:
${form.whatsapp || form.phone}

📧 ${t("registration.whatsappEmail")}:
${form.email || t("registration.notAvailable")}

━━━━━━━━━━━━━━

🎓 ${t("registration.whatsappEducation")}:
${form.education || t("registration.notSpecified")}

🔄 ${t("registration.whatsappPrevious")}:
${previousText}

📢 ${t("registration.whatsappFound")}:
${foundText}

📝 ${t("registration.whatsappNotes")}:
${form.notes || t("registration.none")}

━━━━━━━━━━━━━━

${t("registration.thanks")}
`;

    const whatsappNumber = "9613911681";

    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
      message
    )}`;

    window.open(whatsappUrl, "_blank");
  };

  return (
    <main
      className={`registration-page ${
        isArabic ? "rtl" : "ltr"
      }`}
      dir={isArabic ? "rtl" : "ltr"}
    >
      {/* =====================================================
         LANGUAGE SWITCH
      ===================================================== */}

      <button
        type="button"
        className="language-switch"
        onClick={handleLanguageChange}
        aria-label={
          isArabic
            ? "Switch to English"
            : "التبديل إلى العربية"
        }
      >
        <span className="language-icon">🌐</span>

        <span className="language-text">
          {isArabic ? "EN" : "عربي"}
        </span>
      </button>

      {/* =====================================================
         HERO
      ===================================================== */}

      <section className="registration-hero">
        <div className="hero-decoration red"></div>
        <div className="hero-decoration blue"></div>
        <div className="hero-decoration yellow"></div>

        <div className="registration-hero-content">
          <span className="registration-tag">
            {t("registration.tag")}
          </span>

          <h1>
            {t("registration.heroTitle")}
            <br />

            <span>
              {t("registration.heroTitleHighlight")}
            </span>
          </h1>

          <p>
            {t("registration.heroText")}
          </p>
        </div>
      </section>

      {/* =====================================================
         CONTENT
      ===================================================== */}

      <section className="registration-section">
        <div className="registration-container">

          {/* =====================================================
             INFO
          ===================================================== */}

          <div className="registration-info">

            <span className="registration-label">
              {t("registration.welcome")}
            </span>

            <h2>
              {t("registration.firstStep")}
              <br />
              {t("registration.startsHere")}
            </h2>

            <p>
              {t("registration.infoText")}
            </p>

            <div className="info-items">

              <div className="info-item red-info">
                <div className="info-icon">
                  📚
                </div>

                <span>
                  {t("registration.programs")}
                </span>
              </div>

              <div className="info-item blue-info">
                <div className="info-icon">
                  👨‍🏫
                </div>

                <span>
                  {t("registration.trainers")}
                </span>
              </div>

              <div className="info-item yellow-info">
                <div className="info-icon">
                  🏆
                </div>

                <span>
                  {t("registration.environment")}
                </span>
              </div>

            </div>

            <div className="registration-contact">

              <div className="contact-icon">
                💬
              </div>

              <div>
                <span>
                  {t("registration.contactTitle")}
                </span>

                <strong>
                  03 911 681
                </strong>
              </div>

            </div>

          </div>

          {/* =====================================================
             FORM
          ===================================================== */}

          <form
            className="registration-form"
            onSubmit={handleSubmit}
          >

            {/* =====================================================
               SECTION 01
            ===================================================== */}

            <div className="form-title">

              <span>01</span>

              <div>
                <h2>
                  {t("registration.traineeInfo")}
                </h2>

                <p>
                  {t("registration.basicInfo")}
                </p>
              </div>

            </div>

            {/* COURSE */}

            <div className="form-group full">

              <label>
                {t("registration.course")}
                <span>*</span>
              </label>

              <input
                type="text"
                name="course"
                value={form.course}
                onChange={handleChange}
                placeholder={t(
                  "registration.coursePlaceholder"
                )}
                required
              />

            </div>

            {/* NAME + AGE */}

            <div className="form-row">

              <div className="form-group">

                <label>
                  {t("registration.fullName")}
                  <span>*</span>
                </label>

                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder={t(
                    "registration.fullNamePlaceholder"
                  )}
                  required
                />

              </div>

              <div className="form-group">

                <label>
                  {t("registration.age")}
                  <span>*</span>
                </label>

                <input
                  type="number"
                  name="age"
                  value={form.age}
                  onChange={handleChange}
                  placeholder={t(
                    "registration.agePlaceholder"
                  )}
                  min="3"
                  max="100"
                  required
                />

              </div>

            </div>

            {/* PARENT + GENDER */}

            <div className="form-row">

              <div className="form-group">

                <label>
                  {t("registration.parentName")}
                </label>

                <input
                  type="text"
                  name="parentName"
                  value={form.parentName}
                  onChange={handleChange}
                  placeholder={t(
                    "registration.parentPlaceholder"
                  )}
                />

              </div>

              <div className="form-group">

                <label>
                  {t("registration.gender")}
                </label>

                <select
                  name="gender"
                  value={form.gender}
                  onChange={handleChange}
                >

                  <option value="">
                    {t("registration.choose")}
                  </option>

                  <option value={t("registration.male")}>
                    {t("registration.male")}
                  </option>

                  <option value={t("registration.female")}>
                    {t("registration.female")}
                  </option>

                </select>

              </div>

            </div>

            {/* =====================================================
               SECTION 02
            ===================================================== */}

            <div className="form-title form-title-space">

              <span>02</span>

              <div>

                <h2>
                  {t("registration.contactInfo")}
                </h2>

                <p>
                  {t("registration.contactDescription")}
                </p>

              </div>

            </div>

            {/* PHONE + WHATSAPP */}

            <div className="form-row">

              <div className="form-group">

                <label>
                  {t("registration.phone")}
                  <span>*</span>
                </label>

                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder={t(
                    "registration.phonePlaceholder"
                  )}
                  required
                />

              </div>

              <div className="form-group">

                <label>
                  {t("registration.whatsapp")}
                </label>

                <input
                  type="tel"
                  name="whatsapp"
                  value={form.whatsapp}
                  onChange={handleChange}
                  placeholder={t(
                    "registration.phonePlaceholder"
                  )}
                />

              </div>

            </div>

            {/* EMAIL */}

            <div className="form-group full">

              <label>
                {t("registration.email")}
              </label>

              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder={t(
                  "registration.emailPlaceholder"
                )}
              />

            </div>

            {/* AREA */}

            <div className="form-group full">

              <label>
                {t("registration.area")}
                <span>*</span>
              </label>

              <input
                type="text"
                name="area"
                value={form.area}
                onChange={handleChange}
                placeholder={t(
                  "registration.areaPlaceholder"
                )}
                required
              />

            </div>

            {/* =====================================================
               SECTION 03
            ===================================================== */}

            <div className="form-title form-title-space">

              <span>03</span>

              <div>

                <h2>
                  {t("registration.additionalInfo")}
                </h2>

                <p>
                  {t(
                    "registration.additionalDescription"
                  )}
                </p>

              </div>

            </div>

            {/* EDUCATION */}

            <div className="form-group full">

              <label>
                {t("registration.education")}
              </label>

              <input
                type="text"
                name="education"
                value={form.education}
                onChange={handleChange}
                placeholder={t(
                  "registration.educationPlaceholder"
                )}
              />

            </div>

            {/* PREVIOUS REGISTRATION */}

            <div className="form-group full">

              <label>
                {t("registration.previous")}
              </label>

              <select
                name="previousRegistration"
                value={form.previousRegistration}
                onChange={handleChange}
              >

                <option value="">
                  {t("registration.choose")}
                </option>

                <option value={t("registration.yes")}>
                  {t("registration.yes")}
                </option>

                <option value={t("registration.no")}>
                  {t("registration.no")}
                </option>

              </select>

            </div>

            {/* HOW FOUND */}

            <div className="form-group full">

              <label>
                {t("registration.howFound")}
              </label>

              <select
                name="howFoundUs"
                value={form.howFoundUs}
                onChange={handleChange}
              >

                <option value="">
                  {t("registration.choose")}
                </option>

                <option value="Instagram">
                  Instagram
                </option>

                <option value="Facebook">
                  Facebook
                </option>

                <option value="WhatsApp">
                  WhatsApp
                </option>

                <option value={t("registration.friend")}>
                  {t("registration.friend")}
                </option>

                <option value={t("registration.center")}>
                  {t("registration.center")}
                </option>

                <option value={t("registration.other")}>
                  {t("registration.other")}
                </option>

              </select>

            </div>

            {/* NOTES */}

            <div className="form-group full">

              <label>
                {t("registration.notes")}
              </label>

              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder={t(
                  "registration.notesPlaceholder"
                )}
                rows="5"
              />

            </div>

            {/* =====================================================
               SUBMIT
            ===================================================== */}

            <button
              type="submit"
              className="registration-button"
            >

              <span>
                {t("registration.submit")}
              </span>

              <span className="button-arrow">
                {isArabic ? "←" : "→"}
              </span>

            </button>

            <p className="form-note">
              🔒 {t("registration.privacy")}
            </p>

          </form>

        </div>
      </section>
    </main>
  );
}

export default Registration;